import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  "https://f0usr04663.execute-api.us-east-1.amazonaws.com";

export type UserRole = "OWNER" | "INTEGRATOR" | "ADMIN" | "VIEWER";
export type UserStatus = "ACTIVE" | "INVITED" | "DISABLED" | "DELETED";
export type PasswordMode = "PERMANENT" | "TEMPORARY";

export type TenantUser = {
  userId: string;
  cognitoUsername?: string | null;
  email: string | null;
  displayName: string | null;
  role: UserRole | null;
  status: UserStatus | null;
  cognitoStatus?: string | null;
  enabled?: boolean;
  assignedCameraIds: string[];
  assignedCameraCount: number;
  accessRoleIds?: string[];
  accessRoleNames?: string[];
  createdAt: string | null;
  updatedAt: string | null;
  lastLoginAt: string | null;
};

export type CameraOption = {
  cameraId: string;
  name: string;
  enabled?: boolean;
  streamingStatus?: string;
  accessRoleId?: string | null;
  accessRoleName?: string | null;
};

type ListUsersResponse = {
  items: TenantUser[];
  callerRole: UserRole;
};

type CameraListResponse = {
  items?: Array<Record<string, unknown>>;
};

type UserMutationResponse = TenantUser | { user: TenantUser };

async function getIdToken(): Promise<string> {
  // User-management REST APIs need only the User Pool JWT. Do not enter the
  // Cognito Identity Pool credential path when switching portal accounts.
  const tokens = await cognitoUserPoolsTokenProvider.getTokens();
  const token = tokens?.idToken?.toString();
  if (!token) {
    throw new Error("Your session has expired. Sign out and sign in again.");
  }
  return token;
}

async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getIdToken();
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });

  const text = await response.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    throw new Error(
      payload?.message ||
        payload?.error ||
        text ||
        `Request failed with status ${response.status}`,
    );
  }

  return payload as T;
}

function unwrapUser(payload: UserMutationResponse): TenantUser {
  return "user" in payload ? payload.user : payload;
}

export function listUsers(): Promise<ListUsersResponse> {
  return apiRequest<ListUsersResponse>("/users");
}

export async function createUser(input: {
  email: string;
  displayName: string;
  role: "INTEGRATOR" | "ADMIN" | "VIEWER";
  cameraIds?: string[];
  accessRoleIds?: string[];
  password: string;
  passwordMode: PasswordMode;
}): Promise<TenantUser> {
  const payload = await apiRequest<UserMutationResponse>("/users", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return unwrapUser(payload);
}

export async function updateUser(
  userId: string,
  input: {
    displayName?: string;
    role?: "INTEGRATOR" | "ADMIN" | "VIEWER";
  },
): Promise<TenantUser> {
  const payload = await apiRequest<UserMutationResponse>(
    `/users/${encodeURIComponent(userId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
  return unwrapUser(payload);
}

export async function replaceUserCameras(
  userId: string,
  cameraIds: string[],
): Promise<TenantUser> {
  const payload = await apiRequest<UserMutationResponse>(
    `/users/${encodeURIComponent(userId)}/cameras`,
    {
      method: "PUT",
      body: JSON.stringify({ cameraIds }),
    },
  );
  return unwrapUser(payload);
}

export async function setUserAccessRoles(
  userId: string,
  accessRoleIds: string[],
): Promise<{
  userId: string;
  accessRoleIds: string[];
  accessRoleNames: string[];
}> {
  return apiRequest(
    `/users/${encodeURIComponent(userId)}/access-roles`,
    {
      method: "PUT",
      body: JSON.stringify({ accessRoleIds }),
    },
  );
}

export async function setUserStatus(
  userId: string,
  status: "ACTIVE" | "DISABLED",
): Promise<TenantUser> {
  const payload = await apiRequest<UserMutationResponse>(
    `/users/${encodeURIComponent(userId)}/status`,
    {
      method: "PUT",
      body: JSON.stringify({ status }),
    },
  );
  return unwrapUser(payload);
}

export async function setUserPassword(
  userId: string,
  input: { password: string; passwordMode: PasswordMode },
): Promise<TenantUser> {
  const payload = await apiRequest<UserMutationResponse>(
    `/users/${encodeURIComponent(userId)}/password`,
    {
      method: "PUT",
      body: JSON.stringify(input),
    },
  );
  return unwrapUser(payload);
}

export function deleteUser(
  userId: string,
): Promise<{ deleted: boolean; userId: string }> {
  return apiRequest<{ deleted: boolean; userId: string }>(
    `/users/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
}

export async function listCameraOptions(): Promise<CameraOption[]> {
  const payload = await apiRequest<CameraListResponse>("/cameras");
  const items = Array.isArray(payload?.items) ? payload.items : [];

  return items
    .map((camera) => {
      const cameraId = String(
        camera.cameraId || String(camera.SK || "").replace(/^CAMERA#/, ""),
      ).trim();
      return {
        cameraId,
        name: String(camera.name || camera.cameraName || `Camera ${cameraId}`),
        enabled: typeof camera.enabled === "boolean" ? camera.enabled : undefined,
        streamingStatus:
          typeof camera.streamingStatus === "string"
            ? camera.streamingStatus
            : undefined,
        accessRoleId:
          typeof camera.accessRoleId === "string" ? camera.accessRoleId : null,
        accessRoleName:
          typeof camera.accessRoleName === "string" ? camera.accessRoleName : null,
      };
    })
    .filter((camera) => Boolean(camera.cameraId))
    .sort((a, b) => a.name.localeCompare(b.name));
}
